/**
 * ConfirmCaptchaDialog — 4-digit "catch code" guardrail before destructive
 * / parent-visible actions (publishing results, re-sending WhatsApp blasts).
 *
 * Shows a random 4-digit code the admin must type back. Zero backend —
 * guards against accidental taps, not malicious users (the JWT already does
 * authn/authz).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ShieldAlert, RefreshCw } from "lucide-react";

const _gen = () => String(Math.floor(1000 + Math.random() * 9000));

export default function ConfirmCaptchaDialog({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm before sending",
  description = "This action will message parents on WhatsApp. Enter the 4-digit code below to confirm.",
  confirmLabel = "Confirm & send",
  busy = false,
}) {
  const [code, setCode] = useState(_gen);
  const [typed, setTyped] = useState("");
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  // Regenerate every time the dialog opens so a stale code can't slip through.
  useEffect(() => {
    if (isOpen) {
      setCode(_gen());
      setTyped("");
      setErr("");
      // small defer so the input is mounted before focus
      setTimeout(() => inputRef.current?.focus(), 60);
    }
  }, [isOpen]);

  const digits = useMemo(() => code.split(""), [code]);

  const submit = () => {
    if (typed.trim() !== code) {
      setErr("The code doesn't match. Please re-type it carefully.");
      setTyped("");
      inputRef.current?.focus();
      return;
    }
    onConfirm?.();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="confirm-captcha-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <ShieldAlert className="h-5 w-5 text-amber-500" /> {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="flex items-center justify-between">
            <span className="text-xs uppercase tracking-[0.15em] text-slate-500">Catch code</span>
            <button
              type="button"
              onClick={() => { setCode(_gen()); setTyped(""); setErr(""); inputRef.current?.focus(); }}
              className="text-xs text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 flex items-center gap-1"
              data-testid="captcha-refresh-btn"
              title="Generate a new code"
            >
              <RefreshCw className="h-3 w-3" /> New code
            </button>
          </div>

          <div
            className="flex justify-center gap-2 select-none pointer-events-none"
            aria-label={`Captcha code ${code}`}
            data-testid="captcha-code"
          >
            {digits.map((d, i) => (
              <span
                key={i}
                className="h-14 w-12 flex items-center justify-center rounded-xl bg-slate-900 text-white text-3xl font-bold tracking-wider shadow-inner"
                style={{ fontFamily: "Outfit", transform: `skewY(${((i % 2) ? 3 : -3)}deg)` }}
              >
                {d}
              </span>
            ))}
          </div>

          <input
            ref={inputRef}
            value={typed}
            onChange={(e) => { setTyped(e.target.value.replace(/\D/g, "").slice(0, 4)); setErr(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && typed.length === 4) submit(); }}
            placeholder="Type the 4-digit code"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            className="h-12 w-full text-center text-xl tracking-[0.5em] rounded-xl border border-slate-300 dark:border-slate-600 bg-transparent focus:outline-none focus:ring-2 focus:ring-emerald-400"
            data-testid="captcha-input"
          />
          {err && <p className="text-xs text-rose-500 text-center" data-testid="captcha-error">{err}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="captcha-cancel-btn">Cancel</Button>
          <Button
            onClick={submit}
            disabled={busy || typed.length !== 4}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="captcha-confirm-btn"
          >
            {busy ? "Sending…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
